import { useQuery } from '@apollo/client';
import { faClockRotateLeft, faDatabase, faFileCirclePlus, faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../core/components/Navigation/internal/queries.ts';

import ToggleCard from '../../products/assistants/components/tabs/ToggleCard.tsx';
import {
    DefaultChannelsFields,
    EMPTY_DEFAULT_CHANNELS,
    ErpToolProviderCard,
} from '../../products/assistants/components/tabs/toolProviderCards.tsx';
import { GET_AVAILABLE_TOOL_PROVIDERS } from '../../products/assistants/queries.ts';
import {
    isErpToolProvider,
    ORDER_HISTORY_TOOL_PROVIDER_KEY,
} from '../../products/assistants/components/config/erpToolProviders.ts';

const ErpIcon = withIcon(faDatabase);
const OrderHistoryIcon = withIcon(faClockRotateLeft);
const AttachmentsIcon = withIcon(faPaperclip);
const DocumentCreationIcon = withIcon(faFileCirclePlus);

/** The project's Tools section (ENG-11388): the ERP and the order history its chats may query — set up by managers
 *  who may also configure agents or integrations, the backend enforces the same rule — plus the attachments opt-in
 *  and the channels documents are created in, which any manager sets. Everyone else sees the cards disabled. */
export const ProjectToolsFields = ({
    form,
    readOnly = false,
    canConfigureProviders = false,
}: {
    form: any;
    readOnly?: boolean;
    canConfigureProviders?: boolean;
}) => {
    const { t } = useTranslation('chat');
    const { t: tAssistants } = useTranslation('assistants');

    const { data: toolProvidersData } = useQuery(GET_AVAILABLE_TOOL_PROVIDERS);
    const availableToolProviders = toolProvidersData?.availableToolProviders || [];
    const erpToolProviders = availableToolProviders.filter((provider) => isErpToolProvider(provider.key));
    const isOrderHistoryAvailable = availableToolProviders.some(
        (provider) => provider.key === ORDER_HISTORY_TOOL_PROVIDER_KEY
    );
    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, { skip: readOnly });

    const providersDisabled = readOnly || !canConfigureProviders;

    return (
        <>
            <FormField
                label={t('projects.tools.erp')}
                helpText={!readOnly && !canConfigureProviders ? t('projects.tools.providersAdminOnly') : undefined}
            >
                <div className="flex flex-col gap-4">
                    {erpToolProviders.length > 0 ? (
                        <Controller
                            name="erpToolProvider"
                            control={form.control}
                            render={({ field }) => (
                                <>
                                    {erpToolProviders.map((provider) => (
                                        <ErpToolProviderCard
                                            key={provider.key}
                                            icon={<ErpIcon />}
                                            providerKey={provider.key}
                                            value={field.value?.key === provider.key ? field.value : null}
                                            // one ERP at a time: switching one on replaces the other
                                            onChange={(value) =>
                                                form.setValue('erpToolProvider', value, { shouldDirty: true })
                                            }
                                            isDisabled={providersDisabled}
                                        />
                                    ))}
                                </>
                            )}
                        />
                    ) : (
                        <p className="m-0 text-sm text-content-medium">{t('projects.tools.erpNone')}</p>
                    )}
                    {isOrderHistoryAvailable && (
                        <Controller
                            name="orderHistoryToolProvider"
                            control={form.control}
                            render={({ field }) => (
                                <ToggleCard
                                    icon={<OrderHistoryIcon />}
                                    title={tAssistants('detail.integrationsTab.lookups.orderHistory.name')}
                                    description={tAssistants('detail.integrationsTab.lookups.orderHistory.description')}
                                    isChecked={field.value !== null}
                                    isDisabled={providersDisabled}
                                    onIsCheckedChange={(isChecked) =>
                                        form.setValue(
                                            'orderHistoryToolProvider',
                                            isChecked ? { enabled: true } : null,
                                            {
                                                shouldDirty: true,
                                            }
                                        )
                                    }
                                />
                            )}
                        />
                    )}
                </div>
            </FormField>

            <FormField label={t('projects.tools.actions')} helpText={t('projects.tools.actionsHelp')}>
                <div className="flex flex-col gap-4">
                    <Controller
                        name="attachmentsEnabled"
                        control={form.control}
                        render={({ field }) => (
                            <ToggleCard
                                icon={<AttachmentsIcon />}
                                title={tAssistants('detail.knowledgeTab.attachments.name')}
                                description={tAssistants('detail.knowledgeTab.attachments.description')}
                                isChecked={Boolean(field.value)}
                                isDisabled={readOnly}
                                onIsCheckedChange={(isChecked) =>
                                    form.setValue('attachmentsEnabled', isChecked, { shouldDirty: true })
                                }
                            />
                        )}
                    />
                    <Controller
                        name="defaultChannels"
                        control={form.control}
                        render={({ field }) => (
                            <ToggleCard
                                icon={<DocumentCreationIcon />}
                                title={tAssistants('detail.integrationsTab.actions.documentCreation.name')}
                                description={t('projects.tools.documentCreationHelp')}
                                isChecked={field.value !== null}
                                isDisabled={readOnly}
                                onIsCheckedChange={(isChecked) =>
                                    form.setValue('defaultChannels', isChecked ? { ...EMPTY_DEFAULT_CHANNELS } : null, {
                                        shouldDirty: true,
                                    })
                                }
                                panel={
                                    field.value && (
                                        <DefaultChannelsFields
                                            channelData={channelData}
                                            value={field.value}
                                            onChange={(value) =>
                                                form.setValue('defaultChannels', value, { shouldDirty: true })
                                            }
                                            isDisabled={readOnly}
                                        />
                                    )
                                }
                            />
                        )}
                    />
                </div>
            </FormField>
        </>
    );
};
